// [목적] folio-social 도메인 REST API 클라이언트 — like/unlike/bookmark/unbookmark/comments 8개 엔드포인트 매핑
// [이유] fe-be-wire-up Wave 0 INFRA-05 — FolioPage 소셜 인터랙션 BE 결선 준비.
//         실제 onClick UI 배선은 비범위 (후속 fe-folio-social-wire Spec). 본 카드는 client export 만
// [사이드 이팩트] POST/DELETE like·bookmark·comment: JwtAuthGuard + CsrfGuard 필수 → http.ts 가 CSRF 헤더 자동 첨부.
//                 GET comments: AnonOrAuthenticatedGuard — 비인증 허용.
//                 DELETE comments: 204 반환 → http.ts 에서 undefined 반환
// [수정 시 고려사항] folioId 는 UUID 형식 (BE ParseUUIDPipe 검증). 잘못된 UUID 시 400 반환.
//                   댓글 soft-delete 후 body=null + isDeleted=true tombstone 유지 (FE CommentTree 렌더 고려).

import {
  likeResponseSchema,
  bookmarkResponseSchema,
  folioCommentSchema,
  createCommentInputSchema,
  patchCommentInputSchema,
  commentListQuerySchema,
  type LikeResponse,
  type BookmarkResponse,
  type FolioComment,
  type CreateCommentInput,
  type PatchCommentInput,
  type CommentListQuery,
} from '@gangwoncanvas/types';
import { z } from 'zod';
import { fetchWithEnvelope } from './http.js';

// ─── 내부 헬퍼 ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ─── 페이지네이션 응답 스키마 ──────────────────────────────────────────────────

const commentPageSchema = z.object({
  items: z.array(folioCommentSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().optional(),
});

export type CommentPage = z.infer<typeof commentPageSchema>;

// ─── Like ─────────────────────────────────────────────────────────────────────

/**
 * [목적] POST /folios/:id/like — 좋아요 추가 (idempotent)
 * [이유] INFRA-05: FolioPage 좋아요 버튼 onClick 에서 호출 (후속 fe-folio-social-wire 에서 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. alreadyLiked=true 시 idempotent 200
 * [수정 시 고려사항] folioId UUID 형식 필수 (BE ParseUUIDPipe)
 */
export async function like(folioId: string): Promise<LikeResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/like`, { method: 'POST' });
  return likeResponseSchema.parse(data);
}

/**
 * [목적] DELETE /folios/:id/like — 좋아요 취소 (idempotent)
 * [이유] INFRA-05: 좋아요 토글 off 에서 호출 (후속 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수
 * [수정 시 고려사항] 좋아요 없는 상태에서 unlike → BE idempotent 200 반환
 */
export async function unlike(folioId: string): Promise<LikeResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/like`, { method: 'DELETE' });
  return likeResponseSchema.parse(data);
}

// ─── Bookmark ─────────────────────────────────────────────────────────────────

/**
 * [목적] POST /folios/:id/bookmark — 북마크 추가 (idempotent)
 * [이유] INFRA-05: FolioPage 북마크 버튼 onClick 에서 호출 (후속 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수
 * [수정 시 고려사항] folioId UUID 형식 필수
 */
export async function bookmark(folioId: string): Promise<BookmarkResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/bookmark`, { method: 'POST' });
  return bookmarkResponseSchema.parse(data);
}

/**
 * [목적] DELETE /folios/:id/bookmark — 북마크 취소 (idempotent)
 * [이유] INFRA-05: 북마크 토글 off 에서 호출 (후속 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수
 * [수정 시 고려사항] 북마크 없는 상태에서 unbookmark → BE idempotent 처리
 */
export async function unbookmark(folioId: string): Promise<BookmarkResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/bookmark`, {
    method: 'DELETE',
  });
  return bookmarkResponseSchema.parse(data);
}

// ─── Comments ─────────────────────────────────────────────────────────────────

/**
 * [목적] GET /folios/:id/comments — 댓글 목록 (비인증 허용)
 * [이유] INFRA-05: FolioPage 댓글 섹션 렌더링. AnonOrAuthenticatedGuard 적용
 * [사이드 이팩트] cursor 페이지네이션. parentId 필터 시 해당 부모의 대댓글 전용
 * [수정 시 고려사항] soft-delete tombstone (body=null, isDeleted=true) 포함 응답
 */
export async function getComments(
  folioId: string,
  q?: Partial<CommentListQuery>,
): Promise<CommentPage> {
  const validated = q ? commentListQuerySchema.partial().parse(q) : {};
  const query = buildQuery(validated as Record<string, string | number | undefined>);
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/comments${query}`);
  return commentPageSchema.parse(data);
}

/**
 * [목적] POST /folios/:id/comments — 댓글 생성 (인증 필수)
 * [이유] INFRA-05: FolioPage 댓글 작성 폼에서 호출 (후속 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. parentId 지정 시 대댓글
 * [수정 시 고려사항] text 1~1000자 제한 (createCommentInputSchema)
 */
export async function addComment(folioId: string, body: CreateCommentInput): Promise<FolioComment> {
  const validated = createCommentInputSchema.parse(body);
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/comments`, {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return folioCommentSchema.parse(data);
}

/**
 * [목적] PATCH /folios/:id/comments/:commentId — 댓글 수정 (인증 필수, 작성자 본인만)
 * [이유] INFRA-05: 댓글 수정 인터랙션 준비 (후속 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. 비작성자 수정 시 BE 403 반환
 * [수정 시 고려사항] patchCommentInputSchema 로 text 검증
 */
export async function patchComment(
  folioId: string,
  commentId: string,
  body: PatchCommentInput,
): Promise<FolioComment> {
  const validated = patchCommentInputSchema.parse(body);
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify(validated),
  });
  return folioCommentSchema.parse(data);
}

/**
 * [목적] DELETE /folios/:id/comments/:commentId — 댓글 소프트 삭제 (204 반환)
 * [이유] INFRA-05: 댓글 삭제 인터랙션 준비 (후속 배선)
 * [사이드 이팩트] 204 반환 → http.ts undefined 반환. soft-delete: DB 에 tombstone 유지 (isDeleted=true)
 * [수정 시 고려사항] 권한: author OR folio owner OR admin (BE 서비스 레이어 검증)
 */
export async function deleteComment(folioId: string, commentId: string): Promise<void> {
  await fetchWithEnvelope<void>(`/folios/${folioId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}
